import { LoadingOutlined } from "@ant-design/icons";
import { Alert, Space, Typography } from "antd";
import { ReactNode } from "react";
import { useAppTranslation } from "~/hooks/useAppTranslation";
import { TFunction } from "i18next";

const { Text } = Typography;

/** Built-in copy presets for common tenant long operations (wire when ready). */
export type OperationLoadingBannerPreset = "longOperation" | "submission" | "bulkAction";

export type OperationLoadingBannerProps = {
  /** When false, nothing is rendered. Defaults to true. */
  show?: boolean;
  /** Built-in copy; ignored when message/description are provided. */
  preset?: OperationLoadingBannerPreset;
  message?: ReactNode;
  description?: ReactNode;
  style?: React.CSSProperties;
};

const presetCopy = (
  preset: OperationLoadingBannerPreset,
  t: TFunction,
): { message: ReactNode; description: ReactNode } => {
  switch (preset) {
    case "submission":
      return {
        message: t("Operation_in_Progress"),
        description: (
          <Space direction="vertical" size="small">
            <Text>{t("Submitting_Please_Wait")}</Text>
            <Text strong>{t("Do_Not_Close_Page")}</Text>
          </Space>
        ),
      };
    case "bulkAction":
      return {
        message: t("Operation_in_Progress"),
        description: (
          <Space direction="vertical" size="small">
            <Text>{t("Processing_Selection_Please_Wait")}</Text>
            <Text strong>{t("Do_Not_Close_Page")}</Text>
          </Space>
        ),
      };
    case "longOperation":
    default:
      return {
        message: t("Operation_in_Progress"),
        description: (
          <Space direction="vertical" size="small">
            <Text>{t("Processing_Please_Wait")}</Text>
            <Text strong>{t("Do_Not_Close_Page")}</Text>
          </Space>
        ),
      };
  }
};

/**
 * Banner for long-running mutations (lease submit, payment, bulk cancel, etc.).
 * Use with fetcher/action busy state — not for route navigation or table refresh.
 */
export const OperationLoadingBanner = ({
  show = true,
  preset = "longOperation",
  message,
  description,
  style,
}: OperationLoadingBannerProps) => {
  const { t } = useAppTranslation();

  if (!show) {
    return null;
  }

  const copy = presetCopy(preset, t);

  return (
    <Alert
      type="info"
      banner
      message={message ?? copy.message}
      description={description ?? copy.description}
      icon={<LoadingOutlined />}
      showIcon
      style={{ marginBottom: 16, ...style }}
    />
  );
};
